import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  KeyValuePairs,
  Link,
  Modal,
  Popover,
  SpaceBetween,
  StatusIndicator,
  Steps,
} from '@amzn/awsui-components-react';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { push } from 'connected-react-router';
import OrbitPageGate from './components/OrbitPageGate';
import { getStudyPeriod } from '../../common/constants/study_period';
import { ROUTE_PATH, getRoutePathWithStudyPeriod } from '../../common/constants/route_paths';
import { listComplianceSeasonsActions } from './redux/listComplianceSeasons';
import { closeComplianceSeasonActions } from './redux/closeComplianceSeason';
import { getLaborPopulationOverviewActions } from '../population/components/labor_tab/components/employees_overview/redux/populationOverview';
import { ITEM_TYPE_LABOR } from '../../common/config/api_constants';
import STUDY_SUMMARY_TYPES from '../study_summary/constants/studySummaries';
import { getStudySummaryActions } from '../study_summary/redux';
import SurveysCard from './components/SurveysCard';
import { deriveCloseState } from './utils/seasonState';
import { jurisdictionLabel } from './utils/jurisdiction';
import { RESOURCE, isPageAccessible } from '../auth/constants/rbac_constants';
import QRECard from './components/QRECard';

const getClosePendingMessage = (serverState, studyPeriod, closeId) => {
  if (serverState === 'ARCHIVING') {
    return (
      `Credit year ${studyPeriod} was approved and is being archived now (close ID ${closeId}) — ` +
      'scores are being frozen and the PDF archive generated. This page refreshes automatically.'
    );
  }
  if (serverState === 'CLOSE_APPROVED') {
    return (
      `Credit year ${studyPeriod} close was approved (close ID ${closeId}) and is queued to ` +
      'archive shortly. This page refreshes automatically.'
    );
  }
  return (
    `Close request for credit year ${studyPeriod} submitted (close ID ${closeId}). It is ` +
    'awaiting approval; once approved, scores are frozen and the PDF archive is generated. ' +
    'This page refreshes automatically.'
  );
};

const step = (header, content, status) => ({
  header: (
    <Popover header={header} content={content} triggerType="text" position="right">
      {header}
    </Popover>
  ),
  status,
  statusIconAriaLabel: status,
});

const ComplianceSeasonDetailPage = ({
  studyPeriod,
  seasons,
  fetchSeasons,
  navigateTo,
  laborOverview,
  fetchLaborOverview,
  clearLaborOverview,
  seasonSummary,
  seasonError,
  seasonFetching,
  fetchSeason,
  clearSeason,
  clearClose,
  closeResult,
  isClosing,
  closeError,
  requestClose,
  permissions,
}) => {
  const [showCloseModal, setShowCloseModal] = useState(false);

  // Each deployment serves a single country, so the year identifies the season.
  const currentSeason =
    seasons.find(s => String(get(s, 'studyPeriod')) === String(studyPeriod)) || null;
  const country = get(currentSeason, 'country');
  // Only label the jurisdiction once the season row has loaded; otherwise the default
  // label would briefly mislabel non-US seasons as the default during initial load.
  const jurisdiction = currentSeason ? jurisdictionLabel(country) : null;

  useEffect(() => {
    if (!seasons.length) fetchSeasons();
    fetchSeason();
    fetchLaborOverview({ body: { itemType: ITEM_TYPE_LABOR } });
    // Clear the season, close and labor-overview slices so another year's page
    // doesn't render this year's banners or headcount.
    return () => {
      clearSeason();
      clearClose();
      clearLaborOverview();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyPeriod]);

  // Real labor headcount from getPopulationOverview (overview is a JSON string).
  let laborCount = '—';
  try {
    const overview = JSON.parse(get(laborOverview, 'overview', ''));
    laborCount = get(overview, 'totalEmployees', '—');
  } catch (e) {
    laborCount = '—';
  }

  const seasonTitle = jurisdiction ? `${jurisdiction} - ${studyPeriod}` : `${studyPeriod}`;

  const { serverState, closed, closePending, closeFailed, stateConfirmed } = deriveCloseState({
    seasonSummary,
    seasons,
    studyPeriod,
    closeResult,
  });
  const failureReason = get(seasonSummary, 'failureReason', null);
  const totalBCs = closed ? get(seasonSummary, 'totalBCs', get(currentSeason, 'totalBCs', 0)) : '—';

  // No finer write/operate permission exists for compliance seasons, so the
  // irreversible Close action reuses the page-access check (same permission the
  // page gate uses). A distinct FEATURE_CAN_OPERATE resource would be needed to
  // allow view-only users who cannot close.
  const canOperate = isPageAccessible(permissions, RESOURCE.PAGE_STUDY_SUMMARY);
  // Only offer Close when we have a confirmed state to act on. A null summary
  // (SEASON fetch failed/not loaded) means the state is unknown, so we must not
  // render Close on an assumed-open season.
  const showCloseButton = stateConfirmed && !closed && !closePending && canOperate;

  const go = routePath => () => navigateTo(getRoutePathWithStudyPeriod(routePath, studyPeriod));

  const handleConfirmClose = () => {
    requestClose({ body: { confirmClose: true } });
    setShowCloseModal(false);
  };

  const CLOSE_STATE_MESSAGES = {
    CLOSE_REQUESTED: 'Close requested — awaiting approval',
    CLOSE_APPROVED: 'Close approved — archiving will begin shortly',
    ARCHIVING: 'Archiving in progress — this may take a few minutes',
  };
  const closeStatusMessage = closed
    ? 'Season closed'
    : CLOSE_STATE_MESSAGES[serverState] || (closePending ? 'Close pending approval' : null);

  useEffect(() => {
    if (!closePending) return undefined;
    const timer = setInterval(fetchSeason, 15000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closePending]);

  const renderStatusIndicator = () => {
    if (closed) return <StatusIndicator type="success">Closed</StatusIndicator>;
    if (closePending) {
      return <StatusIndicator type="in-progress">{closeStatusMessage}</StatusIndicator>;
    }
    if (closeFailed) return <StatusIndicator type="error">Close failed</StatusIndicator>;
    // State couldn't be confirmed (SEASON fetch failed / not loaded) — don't
    // claim the season is Open when we don't actually know.
    if (!stateConfirmed)
      return seasonFetching ? (
        <StatusIndicator type="loading">Loading…</StatusIndicator>
      ) : (
        <StatusIndicator type="warning">Status unavailable</StatusIndicator>
      );
    return <StatusIndicator type="info">Open</StatusIndicator>;
  };

  const renderCloseAffordance = () => {
    if (closed || closePending) {
      return (
        <StatusIndicator type={closed ? 'success' : 'in-progress'}>
          {closeStatusMessage}
        </StatusIndicator>
      );
    }
    if (showCloseButton) {
      return (
        <Button onClick={() => setShowCloseModal(true)} loading={isClosing}>
          {closeFailed ? 'Retry close' : 'Close'}
        </Button>
      );
    }
    return null;
  };

  const rndInformationCard = (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" disabled>
                View change log
              </Button>
              {renderCloseAffordance()}
            </SpaceBetween>
          }
        >
          R&amp;D information
        </Header>
      }
    >
      <SpaceBetween size="l">
        {closeFailed && (
          <Alert type="error" header="Close attempt failed">
            {failureReason
              ? `The last close attempt failed: ${failureReason}`
              : 'The last close attempt failed. No data was locked — you can retry the close.'}
          </Alert>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <Steps
            steps={[
              step(
                'Import data',
                'R&D team imports cost-center and employee data; Orbit groups and creates surveys.',
                'success',
              ),
              step(
                'Collect labor surveys',
                'R&D team runs campaigns and collects labor surveys; Orbit computes QREs.',
                closed ? 'success' : 'in-progress',
              ),
              step(
                'Create BCs',
                'R&D Tax team decides which projects to merge and builds Business Components.',
                closed ? 'success' : 'stopped',
              ),
              step(
                'Collect substantiation',
                'R&D team sends, collects and manages substantiation surveys.',
                closed ? 'success' : 'stopped',
              ),
              step(
                'Prepare R&D filing',
                'R&D team closes the compliance season and prepares the filing offline.',
                closed ? 'success' : 'stopped',
              ),
            ]}
          />
          <KeyValuePairs
            columns={1}
            items={[
              { label: 'Jurisdiction', value: jurisdiction || '—' },
              {
                label: 'Status',
                value: renderStatusIndicator(),
              },
              { label: 'Credit year', value: studyPeriod },
            ]}
          />
        </div>
      </SpaceBetween>
    </Container>
  );

  const smallCard = (title, description, onClick) => (
    <Container
      header={
        <Header variant="h2" actions={<Button onClick={onClick}>View all</Button>}>
          {title}
        </Header>
      }
    >
      <Box variant="p" color="text-body-secondary">
        {description}
      </Box>
    </Container>
  );

  const importsSection = (label, routePath, items) => (
    <div>
      <div style={{ marginBottom: 8 }}>
        <Link
          href={getRoutePathWithStudyPeriod(routePath, studyPeriod)}
          fontSize="heading-m"
          onFollow={e => {
            if (e && e.preventDefault) e.preventDefault();
            navigateTo(getRoutePathWithStudyPeriod(routePath, studyPeriod));
          }}
        >
          <span style={{ fontWeight: 700 }}>{label}</span>
        </Link>
      </div>
      <KeyValuePairs columns={3} items={items} />
    </div>
  );

  const importsCard = (
    <Container header={<Header variant="h2">Imports</Header>}>
      <SpaceBetween size="l">
        {importsSection('Population', ROUTE_PATH.POPULATION_PAGE, [
          { label: 'Labor', value: String(laborCount) },
          { label: 'Contract', value: '—' },
        ])}
        {importsSection('Grouping', ROUTE_PATH.GROUPING_PAGE, [
          { label: 'Labor', value: '—' },
          { label: 'Contract', value: '—' },
          { label: 'Supplies', value: '—' },
        ])}
        {importsSection('QNQ', ROUTE_PATH.QNQ_PAGE, [
          { label: 'Cost centers', value: '—' },
          { label: 'Job codes', value: '—' },
          { label: 'Projects', value: '—' },
        ])}
        {importsSection('Business components', ROUTE_PATH.BUSINESS_COMPONENT_PAGE, [
          { label: 'Business components', value: String(totalBCs) },
        ])}
      </SpaceBetween>
    </Container>
  );

  const overviewTab = (
    <SpaceBetween size="l">
      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start' }}
      >
        <SpaceBetween size="m">
          {rndInformationCard}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {smallCard('Reports', 'Download and view reports.', go(ROUTE_PATH.ANALYTICS_PAGE))}
            {smallCard(
              'Email campaign',
              'Manage email campaigns and delivery schedules.',
              go(ROUTE_PATH.NOTIFICATION_PAGE),
            )}
          </div>
        </SpaceBetween>
        {importsCard}
      </div>
      <SurveysCard key={studyPeriod} onViewAll={go(ROUTE_PATH.SURVEY_PAGE)} />
      <QRECard key={studyPeriod} onViewDetails={go(ROUTE_PATH.QRE_PAGE)} country={country} />
    </SpaceBetween>
  );

  const content = (
    <SpaceBetween size="l">
      <Header variant="h1">{seasonTitle}</Header>
      {seasonFetching && !seasonSummary && <Alert type="info">Loading season summary…</Alert>}
      {seasonError && !stateConfirmed && (
        <Alert type="error" header="Could not load season status">
          {`The status for credit year ${studyPeriod} could not be loaded, so close ` +
            'actions are unavailable. Refresh the page to try again.'}
        </Alert>
      )}
      {closePending && (
        <Alert type="info" header="Season close pending approval">
          {getClosePendingMessage(
            serverState,
            studyPeriod,
            get(seasonSummary, 'closeId') || get(closeResult, 'closeId', '—'),
          )}
        </Alert>
      )}
      {closeError && (
        <Alert type="error" header="Season close request failed">
          {`The close request for ${studyPeriod} could not be submitted. ${closeError}`}
        </Alert>
      )}
      {closed && seasonSummary && get(seasonSummary, 'closedAt') && (
        <Alert type="success" header="Compliance season closed">
          {`Credit year ${studyPeriod} closed at ${get(
            seasonSummary,
            'closedAt',
          )} — ${totalBCs} business components captured. Data review features are now active.`}
        </Alert>
      )}

      {overviewTab}

      <Modal
        visible={showCloseModal}
        onDismiss={() => setShowCloseModal(false)}
        header="Close compliance season"
        size="medium"
        closeAriaLabel="Close modal"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowCloseModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleConfirmClose}>
                Close season
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Box variant="p">
            {`Closing the ${studyPeriod} compliance season is irreversible. On confirm:`}
          </Box>
          <ul>
            <li>All substantiation scores are frozen at their current values</li>
            <li>All surveys become view-only — no further edits are possible</li>
            <li>An immutable PDF archive is generated for the season</li>
          </ul>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );

  return (
    <OrbitPageGate
      breadCrumbItems={[
        {
          text: 'Compliance seasons',
          href: ROUTE_PATH.ORBIT_PAGE,
        },
        { text: seasonTitle },
      ]}
    >
      {content}
    </OrbitPageGate>
  );
};

ComplianceSeasonDetailPage.propTypes = {
  studyPeriod: PropTypes.string.isRequired,
  seasons: PropTypes.arrayOf(PropTypes.object),
  fetchSeasons: PropTypes.func.isRequired,
  navigateTo: PropTypes.func.isRequired,
  laborOverview: PropTypes.object,
  fetchLaborOverview: PropTypes.func.isRequired,
  clearLaborOverview: PropTypes.func.isRequired,
  seasonSummary: PropTypes.object,
  seasonError: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  seasonFetching: PropTypes.bool,
  fetchSeason: PropTypes.func.isRequired,
  clearSeason: PropTypes.func.isRequired,
  clearClose: PropTypes.func.isRequired,
  closeResult: PropTypes.object,
  isClosing: PropTypes.bool,
  closeError: PropTypes.string,
  requestClose: PropTypes.func.isRequired,
  permissions: PropTypes.array,
};

ComplianceSeasonDetailPage.defaultProps = {
  seasons: [],
  laborOverview: {},
  seasonSummary: null,
  seasonError: null,
  seasonFetching: false,
  closeResult: null,
  isClosing: false,
  closeError: null,
  permissions: null,
};

const mapStateToProps = state => ({
  studyPeriod: getStudyPeriod(state),
  seasons: get(state, 'entities.complianceSeasons.data.seasons', []),
  laborOverview: get(state, 'entities.population.labor.populationOverview.data', {}) || {},
  seasonSummary: get(
    state,
    ['entities', 'studySummary', 'data', STUDY_SUMMARY_TYPES.SEASON.key],
    null,
  ),
  seasonError: get(
    state,
    ['entities', 'studySummary', 'errorByType', STUDY_SUMMARY_TYPES.SEASON.key],
    null,
  ),
  seasonFetching: get(
    state,
    ['entities', 'studySummary', 'fetchingByType', STUDY_SUMMARY_TYPES.SEASON.key],
    false,
  ),
  permissions: get(state, 'appMetaData.permissions.data', null),
  closeResult: get(state, 'entities.seasonClose.data', null),
  isClosing: get(state, 'entities.seasonClose.isFetching', false),
  closeError: get(state, 'entities.seasonClose.error', null),
});

const mapDispatchToProps = dispatch => ({
  fetchSeasons: () => dispatch(listComplianceSeasonsActions.BEGIN()),
  fetchLaborOverview: payload => dispatch(getLaborPopulationOverviewActions.BEGIN(payload)),
  clearLaborOverview: () => dispatch(getLaborPopulationOverviewActions.CLEAR()),
  fetchSeason: () =>
    dispatch(
      getStudySummaryActions.BEGIN({ body: { summaryType: STUDY_SUMMARY_TYPES.SEASON.key } }),
    ),
  clearSeason: () => dispatch(getStudySummaryActions.CLEAR(STUDY_SUMMARY_TYPES.SEASON.key)),
  clearClose: () => dispatch(closeComplianceSeasonActions.CLEAR()),
  requestClose: payload => dispatch(closeComplianceSeasonActions.BEGIN(payload)),
  navigateTo: path => dispatch(push(path)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ComplianceSeasonDetailPage);
